import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Play, CheckCircle2, ArrowRight, Video, Mic2, Users, 
  Clock, Sparkles, Star, Camera, Tv, Globe, ChevronDown,
  ChevronUp, Zap, TrendingUp, Eye, Share2, Clapperboard
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import PageShareButton from '../components/PageShareButton';

const API = process.env.REACT_APP_BACKEND_URL || '';

const VODCreatorPackage = () => {
  const navigate = useNavigate();
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [seoSettings, setSeoSettings] = useState(null);

  useEffect(() => {
    // Fetch Creator page SEO settings
    const fetchSeoSettings = async () => {
      try {
        const res = await axios.get(`${API}/api/seo/creator/settings`);
        setSeoSettings(res.data);
      } catch (err) {
        console.error('Error fetching creator SEO settings:', err);
      }
    };
    fetchSeoSettings();
  }, []);

  const handleBookNow = () => {
    // Redirect to LTV Studio booking page with VOD creator package
    window.open('https://ltvstudio.co.uk/booking?package=vod_creator', '_blank');
  };

  const features = [
    {
      icon: Camera,
      title: "6K Multi-Camera Setup",
      description: "Three Blackmagic Pyxis cameras capture every angle in stunning quality"
    },
    {
      icon: Mic2,
      title: "Professional Audio",
      description: "Shure MV7 microphones with full acoustic treatment"
    },
    {
      icon: Users,
      title: "Expert On-Site Support",
      description: "Our technicians handle everything so you can focus on creating"
    },
    {
      icon: Tv,
      title: "Global Distribution",
      description: "Your content reaches viewers worldwide on LTV VOD"
    },
    {
      icon: Eye,
      title: "Built-In Audience",
      description: "Tap into our growing viewer base from day one"
    },
    {
      icon: Zap,
      title: "Fast Turnaround",
      description: "Content goes live on LTV VOD within 48-72 hours"
    },
    {
      icon: Share2,
      title: "Social Media Kit",
      description: "Receive 3-5 professionally edited clips for your social media promotion"
    }
  ];

  const comparison = [
    { feature: "Professional Studio Recording", standard: true, vod: true },
    { feature: "6K Multi-Camera Video", standard: true, vod: true },
    { feature: "Shure Professional Microphones", standard: true, vod: true },
    { feature: "On-Site Technical Support", standard: true, vod: true },
    { feature: "Professional Editing", standard: true, vod: true },
    { feature: "Same-Day Content Delivery", standard: true, vod: false },
    { feature: "You Own Full Rights", standard: true, vod: false },
    { feature: "Upload to Your Channel", standard: true, vod: false },
    { feature: "Content on LTV VOD Platform", standard: false, vod: true },
    { feature: "Social Media Clips (3-5)", standard: false, vod: true },
    { feature: "Built-In Audience Exposure", standard: false, vod: true },
    { feature: "Platform Promotion", standard: false, vod: true },
  ];

  const faqs = [
    {
      question: "How is this different from the standard £200/hour package?",
      answer: "With the standard package, you receive your edited content the same day to upload wherever you want. With the VOD Creator Package, your content is published exclusively on the LTV VOD platform where viewers can watch it for free. You get the same premium studio experience at 75% off."
    },
    {
      question: "Do I retain any rights to my content?",
      answer: "LTV Production retains full ownership and rights to all content recorded under this package. However, we provide you with 3-5 professionally edited social media clips (15-60 seconds each) with LTV VOD branding that you can share on your platforms to promote your episode. The full episode will be hosted exclusively on LTV VOD."
    },
    {
      question: "How long until my content goes live?",
      answer: "Your podcast/video will be edited and published on LTV VOD within 48-72 hours of recording. We'll notify you when it's live so you can share with your audience."
    },
    {
      question: "Can viewers watch my content for free?",
      answer: "Yes! All content under this package is available for free viewing on LTV VOD. This helps you reach a wider audience who may not otherwise discover your content."
    },
    {
      question: "What if I want to upgrade to the standard package later?",
      answer: "Absolutely! You can book our standard All-Inclusive package (£200/hour) anytime for content you want to own and distribute yourself. Many creators use both options."
    },
    {
      question: "Is there a minimum booking requirement?",
      answer: "We recommend booking at least 1 hour to ensure quality content production. Most podcast episodes work well in 1-2 hour sessions."
    }
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* SEO Meta Tags - Uses admin-configurable settings */}
      <Helmet>
        <title>{seoSettings?.site_title || 'Creator Package | LTV Production - Professional Video Production'}</title>
        <meta name="description" content={seoSettings?.site_description || 'Book your professional video production package. Studio sessions, live streaming, and content creation services for creators, artists, and businesses.'} />
        <meta name="keywords" content={seoSettings?.site_keywords || 'video production, creator package, studio hire, live streaming, content creation, professional video, LTV Production'} />
        <link rel="canonical" href={seoSettings?.canonical_url || 'https://ltvproduction.com/creator'} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://ltvproduction.com/creator" />
        <meta property="og:title" content={seoSettings?.og_title || 'Creator Package | Professional Video Production'} />
        <meta property="og:description" content={seoSettings?.og_description || 'Book your professional video production package. Studio sessions, live streaming, and content creation services.'} />
        <meta property="og:image" content={seoSettings?.og_image_url || 'https://ltvproduction.com/ltv-social-preview.jpg'} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="LTV VOD" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://ltvproduction.com/creator" />
        <meta name="twitter:title" content={seoSettings?.twitter_title || seoSettings?.og_title || 'Creator Package | LTV VOD'} />
        <meta name="twitter:description" content={seoSettings?.twitter_description || seoSettings?.og_description || 'Professional video production packages for creators, artists, and businesses.'} />
        <meta name="twitter:image" content={seoSettings?.og_image_url || 'https://ltvproduction.com/ltv-social-preview.jpg'} />
      </Helmet>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/vod')}>
              <img 
                src="/ltv-icon.png" 
                alt="LTV Logo" 
                className="w-8 h-8 sm:w-10 sm:h-10 object-contain brightness-0 invert"
              />
              <span className="text-lg sm:text-xl font-bold">LTV <span className="text-purple-400">VOD</span></span>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <PageShareButton page="creator" buttonStyle="outline" />
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/vod')}
                className="hidden sm:flex border-white/20 text-white hover:bg-white/10"
              >
                Browse Content
              </Button>
              <Button 
                size="sm"
                onClick={handleBookNow}
                className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black font-semibold text-xs sm:text-sm px-3 sm:px-4"
              >
                Book Now
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-24 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-900/20 via-black to-black" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-amber-500/10 via-transparent to-transparent" />
        
        <div className="relative max-w-6xl mx-auto text-center">
          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 mb-6">
            <Sparkles className="h-3 w-3 mr-1" />
            Exclusive Creator Opportunity
          </Badge>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            Record at London's #1 Studio
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-400">
              For Just £50/Hour
            </span>
          </h1>
          
          <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-8">
            Get the full LTV Studio experience – 6K video, professional audio, expert support – 
            at 75% off. Your content goes live on LTV VOD for viewers worldwide.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <Button 
              size="lg"
              onClick={handleBookNow}
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black font-bold text-lg px-8 py-6"
            >
              Book Your Session
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => navigate('/vod/browse')}
              className="border-white/20 text-white hover:bg-white/10 px-8 py-6"
            >
              <Play className="mr-2 h-5 w-5" />
              See Creator Content
            </Button>
          </div>

          {/* Price Comparison */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <div className="text-center">
              <p className="text-gray-500 text-sm mb-1">Standard All-Inclusive</p>
              <p className="text-2xl text-gray-400 line-through">£200/hour</p>
            </div>
            <div className="hidden sm:block text-4xl text-amber-500">→</div>
            <div className="text-center">
              <p className="text-amber-400 text-sm mb-1 font-semibold">VOD Creator Package</p>
              <p className="text-4xl font-bold text-amber-400">£50/hour</p>
              <Badge className="mt-2 bg-green-500/20 text-green-400 border-green-500/30">
                Save 75%
              </Badge>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">How It Works</h2>
            <p className="text-gray-400 text-lg">Simple process, professional results</p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: "1", title: "Book Your Slot", desc: "Choose your preferred date and time at LTV Studio" },
              { step: "2", title: "Record Your Content", desc: "Show up, create amazing content with our expert support" },
              { step: "3", title: "We Edit & Publish", desc: "Our team edits and uploads to LTV VOD within 48-72hrs" },
              { step: "4", title: "Reach New Viewers", desc: "Your content is live for our global audience to discover" }
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                  <span className="text-2xl font-bold text-black">{item.step}</span>
                </div>
                <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                <p className="text-gray-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">What's Included</h2>
            <p className="text-gray-400 text-lg">Everything you need for professional content</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <Card key={i} className="bg-[#111111] border-[#222222] hover:border-amber-500/30 transition-colors">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center mb-4">
                    <feature.icon className="h-6 w-6 text-amber-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-gray-400">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20 px-4 bg-gradient-to-b from-[#0a0a0a] to-black">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Package Comparison</h2>
            <p className="text-gray-400 text-lg">Choose what works best for you</p>
          </div>

          <div className="bg-[#111111] rounded-2xl border border-[#222222] overflow-hidden">
            <div className="grid grid-cols-3 bg-[#1a1a1a] p-4 font-bold">
              <div className="text-gray-400">Feature</div>
              <div className="text-center text-white">Standard<br/><span className="text-gray-500 font-normal text-sm">£200/hr</span></div>
              <div className="text-center text-amber-400">VOD Creator<br/><span className="font-normal text-sm">£50/hr</span></div>
            </div>
            {comparison.map((item, i) => (
              <div key={i} className={`grid grid-cols-3 p-4 ${i % 2 === 0 ? 'bg-[#0d0d0d]' : 'bg-[#111111]'}`}>
                <div className="text-gray-300 text-sm">{item.feature}</div>
                <div className="text-center">
                  {item.standard ? (
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  ) : (
                    <span className="text-gray-600">—</span>
                  )}
                </div>
                <div className="text-center">
                  {item.vod ? (
                    <CheckCircle2 className="h-5 w-5 text-amber-400 mx-auto" />
                  ) : (
                    <span className="text-gray-600">—</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Media Kit Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-3xl p-8 md:p-12 border border-green-500/20">
            <div className="text-center mb-8">
              <Badge className="bg-green-500/20 text-green-400 border-green-500/30 mb-4">
                <Share2 className="h-3 w-3 mr-1" />
                Included Free
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                Social Media Kit
              </h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                We create professional clips for you to promote your episode across your social platforms
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-black/40 rounded-xl p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                  <Clapperboard className="h-6 w-6 text-green-400" />
                </div>
                <h3 className="font-bold text-lg mb-2">3-5 Clips</h3>
                <p className="text-gray-400 text-sm">Professionally edited highlight clips (15-60 seconds each)</p>
              </div>
              
              <div className="bg-black/40 rounded-xl p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                  <Video className="h-6 w-6 text-green-400" />
                </div>
                <h3 className="font-bold text-lg mb-2">LTV Branded</h3>
                <p className="text-gray-400 text-sm">Each clip includes LTV VOD branding and episode link</p>
              </div>
              
              <div className="bg-black/40 rounded-xl p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                  <Share2 className="h-6 w-6 text-green-400" />
                </div>
                <h3 className="font-bold text-lg mb-2">Ready to Share</h3>
                <p className="text-gray-400 text-sm">Optimised for Instagram, TikTok, YouTube Shorts & X</p>
              </div>
            </div>
            
            <div className="mt-8 p-4 bg-black/30 rounded-xl border border-white/10">
              <p className="text-center text-gray-300">
                <span className="text-green-400 font-semibold">You promote, we handle the rest.</span> Share clips to drive traffic to your episode on LTV VOD while we manage the full content.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why This Matters */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 mb-6">
                <TrendingUp className="h-3 w-3 mr-1" />
                Creator Benefits
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Why Creators Love This Package
              </h2>
              <div className="space-y-4">
                {[
                  "Affordable access to a premium London studio",
                  "No need to worry about editing or publishing",
                  "Reach new audiences through LTV VOD",
                  "Professional quality content for your portfolio",
                  "Great for creators building their audience",
                  "Perfect for interview series and podcasts"
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-2xl p-8 border border-amber-500/20">
              <div className="text-center">
                <Globe className="h-16 w-16 text-amber-400 mx-auto mb-4" />
                <h3 className="text-2xl font-bold mb-2">Global Reach</h3>
                <p className="text-gray-400 mb-6">
                  Your content joins a growing library on LTV VOD, reaching viewers who are actively looking for quality podcast and video content.
                </p>
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div className="bg-black/50 rounded-xl p-4">
                    <p className="text-3xl font-bold text-amber-400">200+</p>
                    <p className="text-gray-500 text-sm">Happy Creators</p>
                  </div>
                  <div className="bg-black/50 rounded-xl p-4">
                    <p className="text-3xl font-bold text-amber-400">24/7</p>
                    <p className="text-gray-500 text-sm">Content Available</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 px-4 bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Frequently Asked Questions</h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div 
                key={i} 
                className="bg-[#111111] rounded-xl border border-[#222222] overflow-hidden"
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                  className="w-full p-4 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
                >
                  <span className="font-medium text-white pr-4">{faq.question}</span>
                  {expandedFaq === i ? (
                    <ChevronUp className="h-5 w-5 text-amber-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {expandedFaq === i && (
                  <div className="px-4 pb-4">
                    <p className="text-gray-400">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-3xl p-12 border border-amber-500/30">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Ready to Create?
            </h2>
            <p className="text-xl text-gray-400 mb-8">
              Book your £50/hour session at LTV Studio and get your content on LTV VOD.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                size="lg"
                onClick={handleBookNow}
                className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black font-bold text-lg px-8 py-6"
              >
                Book Your Session
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                onClick={() => window.open('https://ltvstudio.co.uk', '_blank')}
                className="border-white/20 text-white hover:bg-white/10 px-8 py-6"
              >
                Learn More About LTV Studio
              </Button>
            </div>
            <p className="text-gray-500 text-sm mt-6">
              Questions? Visit ltvstudio.co.uk or contact us directly.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-white/10">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/vod')}>
            <img 
              src="/ltv-icon.png" 
              alt="LTV Logo" 
              className="w-8 h-8 object-contain brightness-0 invert"
            />
            <span className="font-bold">LTV VOD</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-gray-500">
            <button onClick={() => navigate('/vod')} className="hover:text-white transition-colors">
              Browse VOD
            </button>
            <button onClick={() => window.open('https://ltvstudio.co.uk', '_blank')} className="hover:text-white transition-colors">
              LTV Studio
            </button>
            <button onClick={() => navigate('/live')} className="hover:text-white transition-colors">
              LTV Live
            </button>
          </div>
          <p className="text-sm text-gray-500">© 2026 LTV Production</p>
        </div>
      </footer>
    </div>
  );
};

export default VODCreatorPackage;
